/**
 * §6 確定履歴取込 / §7 簡易予測取込に共通する import mutation 実行の骨格。
 *
 * 両フロー (ガード → mutateAsync → 応答を editRows へ反映 → ダイアログを閉じる、
 * catch: 追加ハンドラ → Aborted (楽観ロック衝突) → その他エラー) が同一骨格を
 * 約50行ずつ複製していた重複を集約する (review-all 指摘)。
 *
 * AC-008 (spec demand-forecast-simple-methods 準拠): §7 の実績データ不足
 * 専用トースト分岐 (extraErrorHandlers) は AC-008 の要求のため維持する。
 * 楽観ロック衝突 (Aborted) の専用トーストは AC-07 の判断どおり削除済み
 * (onAbortedConflict は refetchPage のみ)。
 *
 * ガード条件 (retailerBusinessCd 未確定・目的パラメータ未選択 等) の判定は呼び出し元が
 * mutateAsync を呼ぶ前に済ませ、ガードに引っかかる場合は本関数を呼ばない設計とする。
 */
import { Code, ConnectError } from '@connectrpc/connect'

/** Aborted / その他エラー判定より先に評価する追加ハンドラ (例: §7 FailedPrecondition)。 */
export type ImportMutationErrorHandler = {
  /** true を返した最初のハンドラで処理を終了する */
  matches: (err: unknown) => boolean
  handle: () => void
}

export type ImportMutationParams<TResult> = {
  /** mutation 実行 (caller が create(Schema, {...}) で組んだ request を渡し済みの thunk) */
  mutateAsync: () => Promise<TResult>
  /** 成功応答を editRows へ反映する (index.tsx の applyImportedDraft) */
  applyResult: (result: TResult) => void
  /** 成功後の後始末 (取込確認ダイアログを閉じる・pending state をクリアする 等) */
  onSuccess: () => void
  /** Aborted 判定より先に評価する追加ハンドラ (§7 の FailedPrecondition = 実績データ不足、AC-008) */
  extraErrorHandlers?: ImportMutationErrorHandler[]
  /** 楽観ロック衝突 (Aborted) 時のハンドラ (共通: refetchPage のみ、AC-07 で専用トースト削除済み) */
  onAbortedConflict: () => void
  /** 上記いずれにも該当しないエラー時のハンドラ (通常はエラートースト) */
  onOtherError: () => void
}

/** import mutation 実行の共通フロー。 */
export async function runImportMutation<TResult>({
  mutateAsync,
  applyResult,
  onSuccess,
  extraErrorHandlers = [],
  onAbortedConflict,
  onOtherError,
}: ImportMutationParams<TResult>): Promise<void> {
  try {
    const result = await mutateAsync()
    applyResult(result)
    onSuccess()
  } catch (err) {
    for (const handler of extraErrorHandlers) {
      if (handler.matches(err)) {
        handler.handle()
        return
      }
    }
    if (err instanceof ConnectError && err.code === Code.Aborted) {
      onAbortedConflict()
      return
    }
    onOtherError()
  }
}
